import React, { useEffect, useState, useCallback, useRef } from 'react'
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Switch, 
  message, 
  Spin, 
  Drawer, 
  Tabs, 
  Card, 
  Tag, 
  Alert, 
  Row, 
  Col, 
  Statistic, 
  Segmented, 
  Tooltip, 
  Badge,
  Empty
} from 'antd'
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SettingOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  SafetyOutlined
} from '@ant-design/icons'
import * as Icons from '@ant-design/icons'
import { useConfig } from '../../contexts/ConfigContext'
import { useUser } from '../../contexts/UserContext'
import { useCompany } from '../../contexts/CompanyContext'
import { useSecureRequest } from '../../hooks/useSecureRequest'
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus'
import { ModuleOptionsManager, MODULE_OPTION_TYPES_META } from '@ryo/ui'

// Helper para renderizar íconos dinámicos de Antd
const renderDynamicIcon = (iconName?: string, style?: React.CSSProperties) => {
  if (!iconName) return <AppstoreOutlined style={style} />
  const Component = (Icons as any)[iconName] || AppstoreOutlined
  return <Component style={style} />
}

interface ModuleParametersDrawerContentProps {
  module: any;
  companyId: number;
  adminScope?: string | null;
  securePost: (url: string, data: any) => Promise<any>;
  session: string;
}

const ModuleParametersDrawerContent: React.FC<ModuleParametersDrawerContentProps> = ({
  module,
  companyId,
  adminScope,
  securePost,
  session
}) => {
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [savingTypes, setSavingTypes] = useState(false);
  const [assignedTypes, setAssignedTypes] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>('options');
  const fetchedModuleId = useRef<number | null>(null);

  const fetchAllowedTypes = useCallback(async () => {
    if (!module?.id) return;
    if (fetchedModuleId.current === module.id) return;
    fetchedModuleId.current = module.id;
    setLoadingTypes(true);
    try {
      const res = await securePost('/module/allowed-types/get', {
        session,
        module: module.id
      });
      if (res && Array.isArray(res.allowed_types)) {
        setAssignedTypes(res.allowed_types.map((item: any) => item.type));
      } else {
        setAssignedTypes([]);
      }
    } catch (err) {
      console.error(err);
      message.error('Error al cargar tipos asignados al módulo');
    } finally {
      setLoadingTypes(false);
    }
  }, [module?.id, session, securePost]);

  useEffect(() => {
    fetchAllowedTypes();
  }, [fetchAllowedTypes]);

  const handleToggleType = (typeKey: string, checked: boolean) => {
    if (checked) {
      setAssignedTypes(prev => [...prev, typeKey]);
    } else {
      setAssignedTypes(prev => prev.filter(t => t !== typeKey));
    }
  };

  const handleSaveAllowedTypes = async () => {
    setSavingTypes(true);
    try {
      await securePost('/module/allowed-types/update', {
        session,
        module: module.id,
        types: assignedTypes
      });
      message.success('Tipos de parámetros actualizados para el módulo');
    } catch (err) {
      console.error(err);
      message.error('Error al actualizar tipos asignados');
    } finally {
      setSavingTypes(false);
    }
  };

  const [typeFilter, setTypeFilter] = useState('');
  const allAvailableTypes = Object.keys(MODULE_OPTION_TYPES_META);

  const filteredAvailableTypes = allAvailableTypes.filter(typeKey => {
    if (!typeFilter.trim()) return true;
    const term = typeFilter.toLowerCase().trim();
    const meta = MODULE_OPTION_TYPES_META[typeKey];
    return (
      typeKey.toLowerCase().includes(term) ||
      meta.label.toLowerCase().includes(term) ||
      meta.description.toLowerCase().includes(term)
    );
  });

  const items = [
    {
      key: 'options',
      label: (
        <span>
          Opciones del Módulo{' '}
          <Badge
            count={assignedTypes.length}
            style={{ backgroundColor: '#1890ff', marginLeft: 4 }}
          />
        </span>
      ),
      children: loadingTypes ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : (
        <ModuleOptionsManager
          moduleId={module.id}
          companyId={companyId}
          adminScope={adminScope}
          allowedTypes={assignedTypes}
          securePost={securePost}
          session={session}
        />
      )
    },
    {
      key: 'types',
      label: 'Tipos Asignados al Módulo',
      children: loadingTypes ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin />
        </div>
      ) : (
        <div>
          <Alert
            message="Asignación de Tipos de Parámetros"
            description="Selecciona qué tipos de parámetros están habilitados para este módulo. Los parámetros compartidos a nivel de empresa (impuestos, métodos de pago, unidades de medida) sólo se mostrarán en los módulos que tengan su tipo asignado aquí."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Input
              placeholder="Buscar tipo de parámetro..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              allowClear
              style={{ maxWidth: 320 }}
            />
            <Tag color="blue" style={{ fontSize: 13, padding: '4px 10px' }}>
              {assignedTypes.length} de {allAvailableTypes.length} tipos activados
            </Tag>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12, marginBottom: 20 }}>
            {filteredAvailableTypes.map(typeKey => {
              const meta = MODULE_OPTION_TYPES_META[typeKey];
              const isChecked = assignedTypes.includes(typeKey);
              return (
                <Card key={typeKey} size="small" style={{ borderRadius: 8, borderColor: isChecked ? '#1890ff' : '#e5e7eb', background: isChecked ? '#f0f7ff' : '#ffffff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ paddingRight: 8 }}>
                      <strong style={{ fontSize: '14px' }}>{meta.label}</strong> <Tag color="blue" style={{ marginLeft: 4 }}>{typeKey}</Tag>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{meta.description}</div>
                    </div>
                    <Switch
                      checked={isChecked}
                      onChange={val => handleToggleType(typeKey, val)}
                    />
                  </div>
                </Card>
              );
            })}
          </div>
          <Button type="primary" loading={savingTypes} onClick={handleSaveAllowedTypes}>
            Guardar Asignación de Tipos
          </Button>
        </div>
      )
    }
  ];

  return <Tabs activeKey={activeTab} onChange={key => setActiveTab(key)} items={items} />;
};

function ModulesView() {
  const { config } = useConfig()
  const { user } = useUser()
  const { companies } = useCompany()
  const { securePost } = useSecureRequest()
  const [modules, setModules] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [optionsDrawerVisible, setOptionsDrawerVisible] = useState(false)
  const [selectedModule, setSelectedModule] = useState<any>(null)
  const [form] = Form.useForm()
  const [editingId, setEditingId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')
  const didFetch = useRef(false)

  const fetchModules = useCallback(async (isBackground = false) => {
    if (!config.key) return
    if (!isBackground) setLoading(true)
    try {
      const res = await securePost('/module/list', { session: config.session })
      if (res && Array.isArray(res.modules)) {
        setModules(res.modules)
      } else {
        setModules([])
      }
    } catch (err) {
      console.error(err)
      message.error('Error al cargar módulos')
    } finally {
      if (!isBackground) setLoading(false)
    }
  }, [config.key, config.session, securePost])

  useEffect(() => {
    if (config.key && !didFetch.current) {
      didFetch.current = true
      fetchModules()
    }
  }, [config.key, fetchModules])

  useRefetchOnFocus(fetchModules)

  const handleOpenCreate = () => {
    setEditingId(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleOpenEdit = (record: any) => {
    setEditingId(record.id)
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      app: record.app,
      icon: record.icon,
      path: record.path,
      active: record.active
    })
    setModalVisible(true)
  }

  const handleOpenOptions = (record: any) => {
    setSelectedModule(record)
    setOptionsDrawerVisible(true)
  }

  const handleSubmit = async (values: any) => {
    try {
      if (editingId) {
        await securePost('/module/update', {
          session: config.session,
          key: editingId,
          module: values
        })
        message.success('Módulo actualizado exitosamente')
      } else {
        await securePost('/module/create', {
          session: config.session,
          module: values
        })
        message.success('Módulo creado exitosamente')
      }
      setModalVisible(false)
      fetchModules()
    } catch (err) {
      console.error(err)
      message.error('Error al guardar módulo')
    }
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '¿Está seguro de inhabilitar este módulo?',
      okText: 'Inhabilitar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await securePost('/module/toggle-status', {
            session: config.session,
            idModule: id,
            active: false
          })
          message.success('Módulo inhabilitado exitosamente')
          fetchModules()
        } catch (err) {
          console.error(err)
          message.error('Error al inhabilitar módulo')
        }
      }
    })
  }

  // Módulos filtrados según búsqueda
  const filteredModules = modules.filter(m => {
    const q = searchQuery.toLowerCase()
    return (
      (m.name || '').toLowerCase().includes(q) ||
      (m.app || '').toLowerCase().includes(q) ||
      (m.description || '').toLowerCase().includes(q) ||
      (m.path || '').toLowerCase().includes(q)
    )
  })

  // Métricas
  const totalModules = modules.length
  const activeModules = modules.filter(m => m.active).length
  const inactiveModules = totalModules - activeModules
  const uniqueApps = new Set(modules.map(m => m.app)).size

  const columns = [
    { 
      title: 'Ícono', 
      dataIndex: 'icon', 
      key: 'icon',
      width: 70,
      render: (iconName: string) => (
        <div style={{ 
          width: 38, 
          height: 38, 
          borderRadius: 8, 
          background: '#e6f7ff', 
          color: '#1890ff',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          fontSize: 18
        }}>
          {renderDynamicIcon(iconName)}
        </div>
      )
    },
    { 
      title: 'Nombre', 
      dataIndex: 'name', 
      key: 'name',
      render: (name: string, record: any) => (
        <div>
          <strong style={{ fontSize: '14px', color: '#1f2937' }}>{name}</strong>
          <div style={{ fontSize: '11px', color: '#9ca3af' }}>ID: {record.id}</div>
        </div>
      )
    },
    { 
      title: 'Descripción', 
      dataIndex: 'description', 
      key: 'description',
      ellipsis: true,
      render: (desc: string) => (
        <Tooltip title={desc}>
          <span style={{ color: '#4b5563' }}>{desc}</span>
        </Tooltip>
      )
    },
    { 
      title: 'App Key', 
      dataIndex: 'app', 
      key: 'app',
      render: (app: string) => <Tag color="geekblue" icon={<CodeOutlined />}>{app}</Tag>
    },
    { 
      title: 'Ruta', 
      dataIndex: 'path', 
      key: 'path',
      render: (path: string) => (
        <Tag color="cyan" style={{ fontFamily: 'monospace' }}>
          {path}
        </Tag>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) => (
        <Badge 
          status={active ? 'success' : 'error'} 
          text={<span style={{ fontWeight: 500, color: active ? '#52c41a' : '#ff4d4f' }}>{active ? 'Activo' : 'Inactivo'}</span>} 
        />
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space wrap size="small">
          <Button 
            size="small" 
            type="primary" 
            ghost
            icon={<SettingOutlined />} 
            onClick={() => handleOpenOptions(record)}
          >
            Parámetros
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(record)}>
            Editar
          </Button>
          <Button size="small" type="primary" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
            Eliminar
          </Button>
        </Space>
      )
    }
  ]

  return (
    <div>
      {/* Header General y Métricas */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 600, color: '#111827' }}>
              Gestión de Módulos
            </h3>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Administra las aplicaciones, rutas y parámetros configurables del sistema
            </span>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate} size="large" style={{ borderRadius: 8 }}>
            Crear Módulo
          </Button>
        </div>

        {/* Tarjetas resumen */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ borderRadius: 10, background: '#f8fafc', borderColor: '#e2e8f0' }}>
              <Statistic title="Total Módulos" value={totalModules} prefix={<AppstoreOutlined style={{ color: '#3b82f6' }} />} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ borderRadius: 10, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <Statistic title="Activos" value={activeModules} prefix={<CheckCircleOutlined style={{ color: '#22c55e' }} />} valueStyle={{ color: '#15803d' }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ borderRadius: 10, background: '#fef2f2', borderColor: '#fecaca' }}>
              <Statistic title="Inactivos" value={inactiveModules} prefix={<CloseCircleOutlined style={{ color: '#ef4444' }} />} valueStyle={{ color: '#b91c1c' }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ borderRadius: 10, background: '#f5f3ff', borderColor: '#ddd6fe' }}>
              <Statistic title="Apps Integradas" value={uniqueApps} prefix={<SafetyOutlined style={{ color: '#8b5cf6' }} />} valueStyle={{ color: '#6d28d9' }} />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Barra de Filtros y Control de Vista */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12, background: '#fafafa', padding: '12px 16px', borderRadius: 10, border: '1px solid #f0f0f0' }}>
        <Input
          placeholder="Buscar módulo por nombre, app key o descripción..."
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ maxWidth: 380, borderRadius: 6 }}
          allowClear
        />

        <Segmented
          value={viewMode}
          onChange={(val) => setViewMode(val as 'grid' | 'table')}
          options={[
            { label: 'Tarjetas', value: 'grid', icon: <AppstoreOutlined /> },
            { label: 'Tabla', value: 'table', icon: <UnorderedListOutlined /> }
          ]}
        />
      </div>

      {/* Contenido Principal */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      ) : filteredModules.length === 0 ? (
        <Empty description="No se encontraron módulos registrados" style={{ padding: '40px 0' }} />
      ) : viewMode === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 16 }}>
          {filteredModules.map((item) => (
            <Card
              key={item.id}
              className="module-card"
              size="small"
              style={{
                borderRadius: 12,
                borderColor: item.active ? '#e5e7eb' : '#fca5a5',
                background: item.active ? '#ffffff' : '#fff5f5'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: item.active ? '#e6f7ff' : '#fee2e2',
                    color: item.active ? '#1890ff' : '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22
                  }}>
                    {renderDynamicIcon(item.icon)}
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111827' }}>
                      {item.name}
                    </h4>
                    <Tag color="geekblue" style={{ marginTop: 2, fontSize: '11px' }}>
                      {item.app}
                    </Tag>
                  </div>
                </div>

                <Badge status={item.active ? 'success' : 'error'} text={item.active ? 'Activo' : 'Inactivo'} />
              </div>

              <div style={{ fontSize: '13px', color: '#4b5563', height: 40, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', marginBottom: 12 }}>
                {item.description || 'Sin descripción disponible'}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <CodeOutlined style={{ color: '#9ca3af' }} />
                <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>
                  {item.path}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
                <Button 
                  type="primary" 
                  ghost 
                  size="small" 
                  icon={<SettingOutlined />} 
                  onClick={() => handleOpenOptions(item)}
                  style={{ borderRadius: 6 }}
                >
                  Parámetros
                </Button>

                <Space size="small">
                  <Tooltip title="Editar Módulo">
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(item)} />
                  </Tooltip>
                  <Tooltip title="Inhabilitar Módulo">
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(item.id)} />
                  </Tooltip>
                </Space>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Table dataSource={filteredModules} columns={columns} rowKey="id" scroll={{ x: 'max-content' }} />
      )}

      {/* Modal de Crear / Editar Módulo */}
      <Modal
        title={editingId ? 'Editar Módulo' : 'Crear Módulo'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ active: true }}>
          <Form.Item name="name" label="Nombre del Módulo" rules={[{ required: true, message: 'Ingrese nombre' }]}>
            <Input placeholder="Ej. Inventario / Storage" />
          </Form.Item>
          <Form.Item name="description" label="Descripción" rules={[{ required: true, message: 'Ingrese descripción' }]}>
            <Input.TextArea rows={3} placeholder="Describe el propósito de este módulo..." />
          </Form.Item>
          <Form.Item name="app" label="App Key (Único)" rules={[{ required: true, message: 'Ingrese App Key' }]}>
            <Input placeholder="Ej. ADMIN, STORAGE, SALES" disabled={!!editingId} />
          </Form.Item>
          <Form.Item name="icon" label="Clase del Ícono" rules={[{ required: true, message: 'Ingrese clase de ícono' }]}>
            <Input placeholder="Ej. ShopOutlined, AppstoreOutlined, DatabaseOutlined" />
          </Form.Item>
          <Form.Item name="path" label="Path de Navegación" rules={[{ required: true, message: 'Ingrese path' }]}>
            <Input placeholder="Ej. /admin, /storage" />
          </Form.Item>
          <Form.Item name="active" label="Módulo Activo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Drawer de Parámetros del Módulo */}
      <Drawer
        title={selectedModule ? `Parámetros del Módulo: ${selectedModule.name}` : 'Parámetros del Módulo'}
        width={1020}
        open={optionsDrawerVisible}
        onClose={() => setOptionsDrawerVisible(false)}
        destroyOnHidden
      >
        {selectedModule && (
          <ModuleParametersDrawerContent
            module={selectedModule}
            companyId={companies.current?.id || 1}
            adminScope={user.adminScope || undefined}
            securePost={securePost}
            session={config.session}
          />
        )}
      </Drawer>
    </div>
  )
}

export default ModulesView
